import { Link } from "@tanstack/react-router";
import { Fragment } from "react";

import HeadingWithHighlight from "@/components/heading-with-highlight";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import usePWA from "@/hooks/use-pwa";
import { cn } from "@/lib/utils";

import { ChevronIcon } from "./icons";

type BreadcrumbType =
  | string
  | {
      name: string;
      url?: string;
    };

interface BreadcrumbsProps {
  breadcrumbs: BreadcrumbType[];
  className?: string;
  title?: string;
}

export default function Breadcrumbs({
  breadcrumbs,
  className,
  title,
}: BreadcrumbsProps) {
  const lastIndex = breadcrumbs.length - 1;
  const { homeHref } = usePWA();

  return (
    <>
      <Breadcrumb className={cn("container", className)}>
        <BreadcrumbList className="justify-center sm:gap-3.5">
          <BreadcrumbItem className="text-14/[1.7em] md:text-16/[1.875em]">
            <BreadcrumbLink
              asChild
              className="text-white hover:text-white hover:underline"
            >
              <Link to={homeHref}>Morty</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          {breadcrumbs.map((breadcrumb, index) => {
            let name = breadcrumb as string;
            let url: string | null = null;
            const isCurrent = index === lastIndex;

            if (typeof breadcrumb !== "string") {
              name = breadcrumb.name;
              url = breadcrumb.url || null;
            }

            return (
              <Fragment key={name + url}>
                <BreadcrumbSeparator>
                  <ChevronIcon />
                </BreadcrumbSeparator>
                <BreadcrumbItem className="text-14/[1.7em] md:text-16/[1.875em]">
                  {url ? (
                    <BreadcrumbLink
                      asChild
                      className={cn(
                        "hover:underline",
                        isCurrent
                          ? "text-pale-lavender hover:text-pale-lavender"
                          : "text-white hover:text-white"
                      )}
                    >
                      <Link to={url}>{name}</Link>
                    </BreadcrumbLink>
                  ) : (
                    <BreadcrumbLink>{name}</BreadcrumbLink>
                  )}
                </BreadcrumbItem>
              </Fragment>
            );
          })}
        </BreadcrumbList>
      </Breadcrumb>
      {!!title && (
        <HeadingWithHighlight
          as="h1"
          className="mx-auto mt-[15px] max-w-192 font-semibold text-32/[1.25em] md:mt-10 md:text-60/[1.2em]"
          title={title}
        />
      )}
    </>
  );
}
